import { useTranslation } from 'react-i18next';

import type { ChildDetails } from '@sola/schema/dotcom/children/child.ts';
import { countryToIso } from '@sola/schema/dotcom/shared/geo.ts';

import { key } from '../../organisms-cms/child-bio/utils/bioKeys.tsx';
import { getFamilyList } from '../../organisms-cms/child-bio/utils/familyList.tsx';
import { listFormat } from '../../organisms-cms/child-bio/utils/simpleList.tsx';
import { getAge } from '../../utils/child-bio.ts';

type ChildBioSummaryBaseProps = {
  details: ChildDetails;
  className?: string;
};

type ChildBioSummaryShortProps = ChildBioSummaryBaseProps & {
  short: true;
};

type ChildBioSummaryLongProps = ChildBioSummaryBaseProps & {
  short?: false | undefined;
};

export type ChildBioSummaryProps =
  | ChildBioSummaryShortProps
  | ChildBioSummaryLongProps;

export const ChildBioSummary = ({
  className = 'mb-comfy-sm',
  details,
  short = false,
}: ChildBioSummaryProps) => {
  const { t } = useTranslation(['bio']);

  const { ageInYearsAndMonths, correspondenceLanguage, country, localId } =
    details;

  const age = getAge(ageInYearsAndMonths);

  const countryIso = countryToIso(country, localId);
  const countryKey = `geo:countries.${countryIso}.nameS`;
  const translatedCountry = t(countryKey);
  const languageKey = key('bio:language', correspondenceLanguage);
  const translatedLanguage = t(languageKey as unknown as TemplateStringsArray);

  const favoriteActivities =
    details.interests.thingsILike.length > 0
      ? t('bio:favoriteActivities', {
          context: details.gender,
          favorites: listFormat(
            short
              ? details.interests.thingsILike.slice(0, 3)
              : details.interests.thingsILike,
          ),
        })
      : '';

  const projectActivities =
    details.interests.favoriteProjectActivity.length > 0
      ? t('bio:projectActivities', {
          context: details.gender,
          activities: listFormat(details.interests.favoriteProjectActivity),
        })
      : '';

  return (
    <div className={className}>
      {t(short ? 'bio:summaryShort' : 'bio:summary', {
        context: details.gender,
        childName: details.name.preferred,
        koreanName: details.hangul.preferred || details.name.preferred,
        age,
        country: translatedCountry,
        family: getFamilyList(details.householdList[0].householdMemberList),
        favoriteActivities,
        projectActivities,
        ...(short ? {} : { language: translatedLanguage }),
        interpolation: { escapeValue: false },
      })}
    </div>
  );
};
